import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useCommonAuth } from '../../contexts/CommonAuthContext';
import { config } from '../../config/env';
import { Icon } from '../ui/Icon';
import { Avatar, AvatarFallback } from '../ui/Avatar';
import { Skeleton } from '../ui/Skeleton';
import styles from './UserInfo.module.css';

const UserInfo = () => {
  const { user, logout, loading } = useCommonAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    setIsDropdownOpen(false);
  };

  const handleDashboard = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Navigate to common auth dashboard
    window.location.href = config.COMMON_AUTH_DASHBOARD_URL;
    setIsDropdownOpen(false);
  };

  const handleContactUs = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Navigate to common auth contact page
    window.location.href = `${config.COMMON_AUTH_URL}/contact`;
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    if (!isDropdownOpen && buttonRef.current) {
      // Calculate position for fixed dropdown
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8, // 8px gap below button
        right: window.innerWidth - rect.right // Align to right edge of button
      });
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the container and any portal-rendered dropdown
      const isOutsideContainer = dropdownRef.current && !dropdownRef.current.contains(event.target);
      const isOutsideDropdown = !event.target.closest('.dropdownPanel');
      
      if (isOutsideContainer && isOutsideDropdown) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Shell-first: this renders while auth is still validating, so the
  // null-user guard must come AFTER every hook (an early return above
  // the effect changes the hook count when `user` arrives → React's
  // "rendered more hooks" crash). While validating, hold the button's
  // footprint with an avatar + name skeleton so the chrome doesn't
  // shift when `user` lands.
  if (!user) {
    return loading ? (
      <div className={styles.userInfoContainer}>
        <div className={styles.userInfoSkeleton} aria-hidden="true">
          <Skeleton className={styles.avatarSkeleton} />
          <Skeleton className={styles.nameSkeleton} />
        </div>
      </div>
    ) : null;
  }

  return (
    <>
      <div className={styles.userInfoContainer} ref={dropdownRef}>
        <button 
          ref={buttonRef}
          className={styles.userInfoButton}
          onClick={toggleDropdown}
        >
          <Avatar size="sm">
            <AvatarFallback>
              {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
            </AvatarFallback>
          </Avatar>
          <div className={styles.userDetails}>
            <span className={styles.userName}>{user.email || 'User'}</span>
          </div>
          <div className={styles.dropdownArrow}>
            <Icon name="chevron-down" size={12} />
          </div>
        </button>
      </div>

      {isDropdownOpen && createPortal(
        <div 
          className={`${styles.dropdownPanel} dropdownPanel`}
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
            zIndex: 10000
          }}
        >
          <div className={styles.userInfoDetails}>
            <div className={styles.userInfoItem}>
              <span className={styles.infoLabel}>Company:</span>
              <span className={styles.infoValue}>
                {user.company_name || 'Unknown Company'}
              </span>
            </div>
          </div>
          
          <div className={styles.dropdownDivider} />
          
          <button 
            className={styles.dropdownButton}
            onClick={handleDashboard}
          >
            <span className={styles.menuIcon}>
              <Icon name="home" size={16} />
            </span>
            <span className={styles.menuText}>Dashboard</span>
          </button>
          
          <button
            className={styles.dropdownButton}
            onClick={handleContactUs}
          >
            <span className={styles.menuIcon}>
              <Icon name="mail" size={16} />
            </span>
            <span className={styles.menuText}>Contact Us</span>
          </button>

          <button
            className={styles.logoutButton}
            onClick={handleLogout}
          >
            <span className={styles.menuIcon}>
              <Icon name="logout" size={16} />
            </span>
            <span className={styles.menuText}>Logout</span>
          </button>
        </div>,
        document.body
      )}
    </>
  );
};

export default UserInfo;
